import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useFullWalletAccess, QUERY_KEYS } from "@/hooks/use-queries";
import { 
  Wallet, 
  Users, 
  UserPlus, 
  Mail, 
  Crown, 
  Shield, 
  Heart, 
  Clock, 
  ChevronRight,
  Plus,
  Search,
  Building2,
  Home,
  Users2,
  Check,
  X,
  AlertCircle,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Wallet as WalletType, WalletInvitation, WalletBeneficiary } from "@shared/schema";

interface WalletAccessData {
  owned: WalletType[];
  administering: { wallet: WalletType; role: string; permissions: string[] }[];
  beneficiaryOf: { wallet: WalletType; beneficiary: WalletBeneficiary }[];
  pendingInvitations: (WalletInvitation & { wallet: WalletType })[];
}

function formatCurrency(cents: number | null | undefined): string {
  if (!cents) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function getWalletTypeIcon(walletType: string) {
  switch (walletType) {
    case "individual":
      return <Home className="h-4 w-4" />;
    case "family":
      return <Users className="h-4 w-4" />;
    case "community":
      return <Users2 className="h-4 w-4" />;
    case "organization":
      return <Building2 className="h-4 w-4" />;
    default:
      return <Wallet className="h-4 w-4" />;
  }
}

function WalletCard({ 
  wallet, 
  role, 
  permissions,
  onClick 
}: { 
  wallet: WalletType; 
  role: "owner" | "admin" | "beneficiary";
  permissions?: string[];
  onClick: () => void;
}) {
  const displayName = wallet.organizationName || wallet.patientName || "Unnamed Wallet";
  
  return (
    <Card 
      className="cursor-pointer hover-elevate transition-all"
      onClick={onClick}
      data-testid={`card-wallet-${wallet.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {getWalletTypeIcon(wallet.walletType || "individual")}
            <CardTitle className="text-base line-clamp-1">{displayName}</CardTitle>
          </div>
          <Badge variant={role === "owner" ? "default" : "secondary"}>
            {role === "owner" && <Crown className="h-3 w-3 mr-1" />}
            {role === "admin" && <Shield className="h-3 w-3 mr-1" />}
            {role === "beneficiary" && <Heart className="h-3 w-3 mr-1" />}
            {role}
          </Badge>
        </div>
        {wallet.category && (
          <CardDescription className="text-xs mt-1">{wallet.category}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pb-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">Balance</span>
            <span className="font-medium">{formatCurrency(wallet.balance)}</span>
          </div>
          {wallet.goalAmount && (
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">Goal</span>
              <span className="font-medium">{formatCurrency(wallet.goalAmount)}</span>
            </div>
          )}
          {permissions && permissions.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {permissions.slice(0, 3).map((perm) => (
                <Badge key={perm} variant="outline" className="text-xs">
                  {perm.replace(/_/g, " ")}
                </Badge>
              ))}
              {permissions.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{permissions.length - 3} more
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <Button variant="ghost" size="sm" className="w-full group">
          View Wallet
          <ChevronRight className="h-4 w-4 ml-auto transition-transform group-hover:translate-x-1" />
        </Button>
      </CardFooter>
    </Card>
  );
}

function InvitationCard({ 
  invitation,
  onAccept,
  onDecline,
  isLoading
}: { 
  invitation: WalletInvitation & { wallet: WalletType };
  onAccept: () => void;
  onDecline: () => void;
  isLoading: boolean;
}) {
  const walletName = invitation.wallet?.organizationName || invitation.wallet?.patientName || "Unknown Wallet";
  
  return (
    <Card className="border-primary/20 bg-primary/5" data-testid={`card-invitation-${invitation.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <CardTitle className="text-base line-clamp-1">{walletName}</CardTitle>
          </div>
          <Badge variant="outline" className="text-primary border-primary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        </div>
        <CardDescription className="text-xs mt-1">
          Invited as {invitation.inviteType} by {invitation.invitedByName || "Unknown"}
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-3">
        {invitation.message && (
          <p className="text-sm text-muted-foreground italic mb-3">"{invitation.message}"</p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {getWalletTypeIcon(invitation.wallet?.walletType || "individual")}
          <span className="capitalize">{invitation.wallet?.walletType || "individual"} wallet</span>
          {invitation.wallet?.category && (
            <>
              <span>•</span>
              <span>{invitation.wallet.category}</span>
            </>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-0 gap-2 flex-wrap">
        <Button 
          size="sm" 
          onClick={onAccept}
          disabled={isLoading}
          data-testid={`button-accept-invitation-${invitation.id}`}
        >
          <Check className="h-4 w-4 mr-1" />
          Accept
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onDecline}
          disabled={isLoading}
          data-testid={`button-decline-invitation-${invitation.id}`}
        >
          <X className="h-4 w-4 mr-1" />
          Decline
        </Button>
      </CardFooter>
    </Card>
  );
}

function EmptyState({ type }: { type: "owned" | "admin" | "beneficiary" | "invited" }) {
  const [, navigate] = useLocation();
  
  const content = {
    owned: {
      icon: <Wallet className="h-12 w-12 text-muted-foreground/50" />,
      title: "No wallets yet",
      description: "Create your first HealthWallet to start receiving contributions for healthcare needs.",
      action: (
        <Button onClick={() => navigate("/wallet/create")} data-testid="button-create-first-wallet">
          <Plus className="h-4 w-4 mr-2" />
          Create Wallet
        </Button>
      ),
    },
    admin: {
      icon: <Shield className="h-12 w-12 text-muted-foreground/50" />,
      title: "No admin access",
      description: "You haven't been invited to manage any wallets yet.",
      action: null,
    },
    beneficiary: {
      icon: <Heart className="h-12 w-12 text-muted-foreground/50" />,
      title: "Not a beneficiary",
      description: "You haven't been added as a beneficiary to any family, community, or organization wallets.",
      action: null,
    },
    invited: {
      icon: <Mail className="h-12 w-12 text-muted-foreground/50" />,
      title: "No pending invitations",
      description: "You don't have any pending wallet invitations at this time.",
      action: null,
    },
  };
  
  const { icon, title, description, action } = content[type];
  
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {icon}
      <h3 className="mt-4 text-lg font-medium" data-testid={`text-empty-${type}`}>{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2 mt-2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3 mt-2" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-8 w-full" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function WalletDashboard() {
  usePageTitle("Wallet Overview", "Overview of all your health wallets, balances, and recent transactions.");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  const { data: accessData, isLoading, error } = useFullWalletAccess();
  
  const acceptMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      return await apiRequest("POST", `/api/wallet-invitations/${invitationId}/accept`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.fullWalletAccess });
      toast({
        title: "Invitation accepted",
        description: "You now have access to this wallet.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to accept invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const declineMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      return await apiRequest("POST", `/api/wallet-invitations/${invitationId}/decline`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.fullWalletAccess });
      toast({
        title: "Invitation declined",
        description: "The invitation has been declined.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to decline invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const handleWalletClick = (walletId: string) => {
    navigate(`/wallet/${walletId}`);
  };
  
  const totalWallets = (accessData?.owned?.length || 0) + 
                       (accessData?.administering?.length || 0) + 
                       (accessData?.beneficiaryOf?.length || 0);
  
  const pendingCount = accessData?.pendingInvitations?.length || 0;
  
  if (error) {
    return (
      <div className="container max-w-6xl py-8">
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-4 py-8">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <h3 className="font-semibold">Failed to load wallet access</h3>
              <p className="text-sm text-muted-foreground">
                {(error as Error).message || "An unexpected error occurred"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container max-w-6xl py-8">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="text-wallet-dashboard-title">
              <Wallet className="h-6 w-6" />
              My Wallets
            </h1>
            <p className="text-muted-foreground mt-1">
              {isLoading ? (
                <Skeleton className="h-4 w-48" />
              ) : (
                `${totalWallets} wallet${totalWallets !== 1 ? 's' : ''} accessible`
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => navigate("/wallet/discover")}
              data-testid="button-discover-wallets"
            >
              <Search className="h-4 w-4 mr-2" />
              Discover Wallets
            </Button>
            <Button 
              onClick={() => navigate("/wallet/create")}
              data-testid="button-create-wallet"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Wallet
            </Button>
          </div>
        </div>
        
        {pendingCount > 0 && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">
                    You have {pendingCount} pending invitation{pendingCount !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Check the "Invited" tab to accept or decline
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const tab = document.querySelector('[data-value="invited"]');
                    if (tab instanceof HTMLElement) tab.click();
                  }}
                >
                  View Invitations
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Tabs defaultValue="owned" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="owned" className="gap-1" data-testid="tab-owned">
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Owned</span>
              {accessData?.owned && accessData.owned.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {accessData.owned.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="admin" className="gap-1" data-testid="tab-admin">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Managing</span>
              {accessData?.administering && accessData.administering.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {accessData.administering.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="beneficiary" className="gap-1" data-testid="tab-beneficiary">
              <Heart className="h-4 w-4" />
              <span className="hidden sm:inline">Beneficiary</span>
              {accessData?.beneficiaryOf && accessData.beneficiaryOf.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {accessData.beneficiaryOf.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="invited" className="gap-1" data-value="invited" data-testid="tab-invited">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Invited</span>
              {pendingCount > 0 && (
                <Badge variant="default" className="ml-1">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="owned">
            {isLoading ? (
              <LoadingSkeleton />
            ) : accessData?.owned && accessData.owned.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accessData.owned.map((wallet) => (
                  <WalletCard
                    key={wallet.id}
                    wallet={wallet}
                    role="owner"
                    onClick={() => handleWalletClick(wallet.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="owned" />
            )}
          </TabsContent>
          
          <TabsContent value="admin">
            {isLoading ? (
              <LoadingSkeleton />
            ) : accessData?.administering && accessData.administering.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accessData.administering.map(({ wallet, role, permissions }) => (
                  <WalletCard
                    key={wallet.id}
                    wallet={wallet}
                    role="admin"
                    permissions={permissions}
                    onClick={() => handleWalletClick(wallet.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="admin" />
            )}
          </TabsContent>
          
          <TabsContent value="beneficiary">
            {isLoading ? (
              <LoadingSkeleton />
            ) : accessData?.beneficiaryOf && accessData.beneficiaryOf.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accessData.beneficiaryOf.map(({ wallet, beneficiary }) => (
                  <WalletCard
                    key={wallet.id}
                    wallet={wallet}
                    role="beneficiary"
                    permissions={beneficiary.spendingLimit ? [`Limit: ${formatCurrency(beneficiary.spendingLimit)}`] : undefined}
                    onClick={() => handleWalletClick(wallet.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="beneficiary" />
            )}
          </TabsContent>
          
          <TabsContent value="invited">
            {isLoading ? (
              <LoadingSkeleton />
            ) : accessData?.pendingInvitations && accessData.pendingInvitations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accessData.pendingInvitations.map((invitation) => (
                  <InvitationCard
                    key={invitation.id}
                    invitation={invitation}
                    onAccept={() => acceptMutation.mutate(invitation.id)}
                    onDecline={() => declineMutation.mutate(invitation.id)}
                    isLoading={acceptMutation.isPending || declineMutation.isPending}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="invited" />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
